import React, { Suspense, lazy } from 'react';
import Spline from '@splinetool/react-spline';
import { ArrowRight, CheckCircle, Star, Globe, Users, TrendingUp, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Home.css';
import { services, testimonials, stats, whatsappNumber } from '../data/mock';

// Icon mapping
const iconMap = {
  CheckCircle,
  Star,
  Globe,
  Users
};

const Home = () => {
  return (
    <div className="home">
      {/* Hero Section with Spline 3D */}
      <section className="hero-section">
        <div className="container hero-container">
          <div className="hero-content fade-in">
            <h1 className="display-huge mb-3">
              We Build Intelligent Digital Systems That <span className="gradient-text">Scale Your Business</span>
            </h1>
            <p className="body-large mb-4">
              Transform your vision into reality with cutting-edge technology. Serving businesses, schools, and colleges with AI-powered automation, enterprise-grade applications, and solutions that drive measurable results.
            </p>
            <div className="hero-cta">
              <Link to="/contact" className="btn-primary">
                Book Free Strategy Call
                <ArrowRight size={20} />
              </Link>
              <Link to="/portfolio" className="btn-secondary">
                View Our Work
              </Link>
            </div>
            <div className="hero-stats mt-5">
              {stats.map((stat) => {
                const Icon = iconMap[stat.icon];
                return (
                  <div key={stat.label} className="stat-item">
                    <Icon size={24} className="stat-icon" />
                    <div>
                      <div className="stat-value">{stat.value}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="hero-visual">
            <div className="spline-container">
              <Suspense fallback={<div className="spline-loading">Loading 3D Experience...</div>}>
                <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
              </Suspense>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section services-preview">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-medium mb-2">Our Services</h2>
            <p className="body-large">Comprehensive digital solutions tailored to your business needs</p>
          </div>
          <div className="services-grid">
            {services.slice(0, 6).map((service, index) => (
              <div key={service.id} className="glass-card service-card fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="service-icon">
                  <Zap size={32} />
                </div>
                <h3 className="heading-2 mb-2">{service.title}</h3>
                <p className="body-medium mb-3">{service.description}</p>
                <Link to="/services" className="service-link">
                  Learn More
                  <ArrowRight size={18} />
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-5">
            <Link to="/services" className="btn-primary">
              View All Services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* AI & Automation Highlight */}
      <section className="section ai-section">
        <div className="container">
          <div className="ai-grid">
            <div className="ai-content slide-in-left">
              <h2 className="display-medium mb-3">
                AI Agents & <span className="gradient-text">Intelligent Automation</span>
              </h2>
              <p className="body-large mb-4">
                Deploy smart AI agents that understand context, learn from interactions, and automate complex business processes. Our AI solutions improve operational efficiency by up to 60% while dramatically enhancing productivity.
              </p>
              <ul className="ai-features">
                <li>
                  <CheckCircle size={20} className="feature-icon" />
                  <span>24/7 automated customer support with natural language understanding</span>
                </li>
                <li>
                  <CheckCircle size={20} className="feature-icon" />
                  <span>Intelligent workflow automation that adapts to your business</span>
                </li>
                <li>
                  <CheckCircle size={20} className="feature-icon" />
                  <span>Real-time decision making with predictive analytics</span>
                </li>
                <li>
                  <CheckCircle size={20} className="feature-icon" />
                  <span>Seamless integration with existing systems</span>
                </li>
              </ul>
              <Link to="/services" className="btn-primary mt-4">
                Explore AI Solutions
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="ai-visual slide-in-right">
              <img 
                src="https://images.unsplash.com/photo-1770170389700-eb0f9b910ed8" 
                alt="AI Technology" 
                className="ai-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section testimonials-section">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-medium mb-2">Trusted by Industry Leaders</h2>
            <p className="body-large">Real results from real clients</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <div key={testimonial.id} className="glass-card testimonial-card fade-in" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="stars mb-2">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="var(--accent-primary)" color="var(--accent-primary)" />
                  ))}
                </div>
                <p className="body-medium mb-3">"{testimonial.text}"</p>
                <div className="results-badge mb-3">
                  <TrendingUp size={16} />
                  <span>{testimonial.results}</span>
                </div>
                <div className="testimonial-author">
                  <img src={testimonial.image} alt={testimonial.name} className="author-image" />
                  <div>
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content text-center">
            <h2 className="display-medium mb-3">Ready to Transform Your Business?</h2>
            <p className="body-large mb-4">
              Let's discuss how we can help you achieve exponential growth with intelligent digital solutions.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary btn-large">
                Book Free Strategy Call
                <ArrowRight size={22} />
              </Link>
              <a 
                href={`https://wa.me/${whatsappNumber.replace(/\+/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary btn-large"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
